import { useMemo, useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import { fakeFuseSearchObject } from "@/__mocks__/useFuseSearch";
import type { FuseSearchMockObject } from "@/__mocks__/useFuseSearch";
import type { UseFuseSearchProps } from "@/hooks/useFuseSearch";
import useFuseSearch from "@/hooks/useFuseSearch";
import { FAIconName, Text, TextField } from "@/ui/core";
import { FlatList, VStack } from "@/ui/layout";

interface FuseSearchDemoProps {
  isDaySearchEnabled?: boolean;
  isDescriptionSearchEnabled?: boolean;
  isHostSearchEnabled?: boolean;
  isTitleSearchEnabled?: boolean;
  threshold?: number;
}

const mockData: FuseSearchMockObject[] = Array.from({ length: 10 }, () => {
  return fakeFuseSearchObject();
});

const Component = ({
  isDaySearchEnabled,
  isDescriptionSearchEnabled,
  isHostSearchEnabled,
  isTitleSearchEnabled,
  threshold,
}: FuseSearchDemoProps) => {
  const [searchValue, setSearchValue] = useState<string>("");

  const searchKeys: UseFuseSearchProps<FuseSearchMockObject>["searchKeys"] =
    useMemo(() => {
      const keys: (keyof FuseSearchMockObject)[] = [];

      if (isDaySearchEnabled) {
        keys.push("day");
      }

      if (isDescriptionSearchEnabled) {
        keys.push("description");
      }

      if (isHostSearchEnabled) {
        keys.push("host");
      }

      if (isTitleSearchEnabled) {
        keys.push("title");
      }

      return keys;
    }, [
      isDaySearchEnabled,
      isDescriptionSearchEnabled,
      isHostSearchEnabled,
      isTitleSearchEnabled,
    ]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const results = useFuseSearch<FuseSearchMockObject>({
    data: mockData,
    searchKeys,
    searchValue,
    threshold,
  });

  return (
    <VStack gap={8} width="100%">
      <VStack>
        <Text testID="" variant="bodyMedium">
          Search Keys:
        </Text>
        {/* eslint-disable-next-line @typescript-eslint/no-base-to-string */}
        <Text testID="">{searchKeys.join(", ")}</Text>
      </VStack>
      <TextField
        accessibilityLabel="Search"
        autoCapitalize="sentences"
        icon={FAIconName.Search}
        isAutoCorrect={false}
        onChange={handleSearchChange}
        placeholder="Search"
        testID=""
        value={searchValue}
      />
      <FlatList
        data={results}
        isDataLoading={false}
        isRefreshControlLoading={false}
        isScrollEnabled={false}
        renderItem={({ item }) => (
          <VStack>
            <Text testID="">{JSON.stringify(item, null, 2)}</Text>
          </VStack>
        )}
        testID=""
      />
    </VStack>
  );
};

const StoryMeta: Meta<FuseSearchDemoProps> = {
  title: "Hooks/FuseSearch",
  component: Component,
  argTypes: {
    isDaySearchEnabled: {
      control: "boolean",
    },
    isDescriptionSearchEnabled: {
      control: "boolean",
    },
    isHostSearchEnabled: {
      control: "boolean",
    },
    isTitleSearchEnabled: {
      control: "boolean",
    },
    threshold: {
      max: 1,
      min: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      range: true,
      step: 0.1,
    },
  },
  args: {
    isDaySearchEnabled: true,
    isDescriptionSearchEnabled: true,
    isHostSearchEnabled: true,
    isTitleSearchEnabled: true,
    threshold: 0.2,
  },
};

type Story = StoryObj<FuseSearchDemoProps>;

export default StoryMeta;
export const Primary: Story = {};
